/**
 * AdminSignup.jsx — Iter 57
 * Request admin access. Super admin (Sid) approves from /admin/admins.
 */
import React, { useState } from 'react';
import api from '@/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle, ArrowLeft } from '@phosphor-icons/react';

const AdminSignup = () => {
  const [form, setForm] = useState({ email: '', full_name: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post('/admin/signup-request', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] text-slate-100 flex items-center justify-center p-4" data-testid="admin-signup-page">
      <div className="w-full max-w-md bg-[#111827] border border-slate-800 rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={22} weight="fill" className="text-emerald-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold">Founderboard</div>
            <h1 className="text-lg font-heading font-bold">Request admin access</h1>
          </div>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="admin-signup-done">
            <CheckCircle size={40} weight="fill" className="text-emerald-400 mx-auto mb-3" />
            <h2 className="text-lg font-heading font-bold mb-2">Request submitted</h2>
            <p className="text-sm text-slate-400 mb-6">Sid will review your request and email you once approved.</p>
            <Link to="/admin/login" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300"><ArrowLeft size={11} weight="bold" /> Back to sign in</Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-5">Tell us who you are and why you need admin access. Founder&rsquo;s office reviews every request within 24 hours.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Full name</label>
                <input required value={form.full_name} data-testid="admin-signup-name"
                  onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                  className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Work email</label>
                <input type="email" required value={form.email} data-testid="admin-signup-email"
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Reason for access</label>
                <textarea rows={3} value={form.reason} data-testid="admin-signup-reason"
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                  className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <button type="submit" disabled={busy} data-testid="admin-signup-submit"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                {busy ? 'Submitting…' : 'Request access'}
              </button>
            </form>
            <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-500 text-center">
              <Link to="/admin/login" className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-0.5" data-testid="admin-back-to-login"><ArrowLeft size={9} weight="bold" /> Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSignup;
